// DashboardCallLogsDrawer.tsx - Simple drawer component that takes props
import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Input, List, Spin, Button, Avatar, Tag, message } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { PhoneIncoming, PhoneOutgoing, PhoneMissed, User, Volume2 } from 'lucide-react';
import moment from 'moment';
import debounce from 'lodash/debounce';
import ReactAudioPlayer from 'react-audio-player';
import { useSelector } from 'react-redux';

// Import API service
import { getCallLogs, CallLog, PaginationQuery } from '../../services/callLogsService';

interface DashboardCallLogsDrawerProps {
  visible: boolean;
  onClose: () => void;
}

const DashboardCallLogsDrawer: React.FC<DashboardCallLogsDrawerProps> = ({ visible, onClose }) => {
  console.log('📱 DashboardCallLogsDrawer rendered, visible:', visible);

  // Access Redux store to get bucketUrl for audio files
  const StoreData = useSelector((state: any) => state?.rootReducers);
  const bucketUrl = StoreData?.userData?.userDetails?.bucketUrl;

  // Simple state
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  // Get call type details
  const getCallTypeDetails = (type: string) => {
    console.log('📞 Getting call type for:', type);
    switch (type) {
      case 'incoming':
        return { icon: <PhoneIncoming size={16} color="#28a745" />, color: '#28a745', label: 'Incoming' };
      case 'outgoing':
        return { icon: <PhoneOutgoing size={16} color="#007bff" />, color: '#007bff', label: 'Outgoing' };
      case 'missed':
        return { icon: <PhoneMissed size={16} color="#dc3545" />, color: '#dc3545', label: 'Missed' };
      default:
        return { icon: <PhoneIncoming size={16} color="#6c757d" />, color: '#6c757d', label: 'Unknown' };
    }
  };

  // Debounced search function - MUST be declared before handleSearch
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      console.log('🔍 Debounced search triggered:', searchTerm);
      setCurrentPage(1);
      fetchCallLogs(1, searchTerm, false);
    }, 500),
    []
  );

  // Fetch call logs
  const fetchCallLogs = async (page: number = 1, search: string = '', isLoadMore: boolean = false) => {
    console.log('🔄 fetchCallLogs called:', { page, search, isLoadMore });

    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const query: PaginationQuery = {
        page: page,
        perPage: 20,
        search: search.trim() || undefined
      };

      console.log('📡 API call with:', query);
      const response = await getCallLogs(query);
      console.log('✅ API response:', response);

      if (isLoadMore) {
        setCallLogs(prev => [...prev, ...response.data]);
        console.log('📄 Added more logs, total:', callLogs.length + response.data.length);
      } else {
        setCallLogs(response.data);
        console.log('📋 Set new logs:', response.data.length);
      }

      setTotal(response.total);
      setCurrentPage(page);
      setHasMore((page * 20) < response.total);

      console.log('📊 Updated pagination:', { currentPage: page, total: response.total, hasMore: (page * 20) < response.total });

    } catch (error) {
      console.error('❌ API error:', error);
      message.error('Failed to fetch call logs');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load initial data when drawer opens
  useEffect(() => {
    if (visible) {
      console.log('🔓 Drawer opened, fetching initial data');
      setSearchText('');
      setCurrentPage(1);
      fetchCallLogs(1, '', false);
    } else {
      console.log('🔒 Drawer closed, clearing data');
      setCallLogs([]);
      setSearchText('');
      setCurrentPage(1);
      setHasMore(true);
      setTotal(0);
      // Cancel any pending debounced search
      debouncedSearch.cancel();
    }
  }, [visible, debouncedSearch]);

  // Handle search input change
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    console.log('🔍 Search input changed:', value);
    setSearchText(value);
    
    // Cancel previous debounced call and start new one
    debouncedSearch.cancel();
    if (value.trim()) {
      debouncedSearch(value.trim());
    } else {
      // If search is cleared, fetch immediately
      setCurrentPage(1);
      fetchCallLogs(1, '', false);
    }
  };

  // Handle load more
  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    console.log('📄 Load more page:', nextPage);
    fetchCallLogs(nextPage, searchText, true);
  };

  // Handle refresh
  const handleRefresh = () => {
    console.log('🔄 Refresh');
    debouncedSearch.cancel(); // Cancel any pending search
    setSearchText('');
    setCurrentPage(1);
    fetchCallLogs(1, '', false);
  };

  // Render call log item
  const renderCallLog = (log: CallLog) => {
    const callType = getCallTypeDetails(log.type);
    
    // Check if call has recording files - fix type checking
    const hasRecording = Array.isArray(log.files) && log.files.length > 0;
    const recordingFile = (Array.isArray(log.files) && log.files.length > 0) ? log.files[0] : null;
    
    console.log('🎵 Call log recording check:', {
      callId: log._id,
      hasRecording,
      recordingFile,
      bucketUrl,
      filesArray: log.files
    });
    
    return (
      <List.Item
        key={log._id}
        style={{
          background: '#f8f9fa',
          marginBottom: '8px',
          borderRadius: '8px',
          padding: '12px',
          border: '1px solid #e9ecef'
        }}
      >
        <div style={{ width: '100%' }}>
          <List.Item.Meta
            avatar={<Avatar icon={<User size={20} />} style={{ backgroundColor: '#1890ff' }} />}
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: 600, fontSize: '14px' }}>
                  {log.lead?.name || log.userContact?.firstName || 'Unknown'}
                </span>
                {log.concurrentCall && log.concurrentCall > 1 && (
                  <Tag color="blue" style={{ fontSize: '11px' }}>
                    {log.concurrentCall} Calls
                  </Tag>
                )}
                {hasRecording && (
                  <Tag color="green" style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Volume2 size={12} />
                    Recording
                  </Tag>
                )}
              </div>
            }
            description={
              <div>
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                  {log.phone}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#888' }}>
                    {moment(log.startedAt).format('MMM Do, h:mm A')}
                  </span>
                  <Tag color={callType.color} style={{ fontSize: '11px', margin: 0 }}>
                    {callType.icon} {callType.label}
                  </Tag>
                  <span style={{ fontSize: '12px', fontWeight: 500, color: '#333' }}>
                    {log.duration ? moment.utc(log.duration * 1000).format('mm:ss') : '00:00'}
                  </span>
                </div>
                
                {/* Audio recording player */}
                {hasRecording && recordingFile && bucketUrl && (
                  <div style={{
                    background: '#f1f5f9',
                    borderRadius: '8px',
                    padding: '8px',
                    marginTop: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      marginBottom: '6px'
                    }}>
                      <Volume2 size={14} color="#475569" />
                      <span style={{ fontSize: '12px', color: '#475569', fontWeight: 500 }}>
                        Call Recording
                      </span>
                    </div>
                    <ReactAudioPlayer
                      src={`${bucketUrl}${recordingFile}`}
                      controls
                      style={{
                        width: '100%',
                        height: '32px',
                        backgroundColor: 'transparent'
                      }}
                      preload="metadata"
                      onError={(e) => {
                        console.error('🎵 Audio playback error:', e);
                        message.error('Failed to load call recording');
                      }}
                    />
                  </div>
                )}
              </div>
            }
          />
        </div>
      </List.Item>
    );
  };

  return (
    <Drawer
      title="Call Logs"
      placement="right"
      onClose={onClose}
      visible={visible}
      width={600}
      destroyOnClose={true}
      maskClosable={false}
      closable={true}
    >
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        
        {/* Search controls */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <Input
            placeholder="Search by name or phone"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={handleSearch}
            allowClear
            style={{ flex: 1 }}
            disabled={loading || loadingMore}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
            disabled={loadingMore}
          >
            Refresh
          </Button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {/* Loading */}
          {loading && callLogs.length === 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <Spin tip="Loading call logs..." />
            </div>
          )}

          {/* Call logs */}
          {callLogs.length > 0 && (
            <>
              <List
                dataSource={callLogs}
                renderItem={renderCallLog}
                style={{ background: 'transparent' }}
              />
              
              {/* Load more */}
              {hasMore && (
                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  <Button
                    onClick={handleLoadMore}
                    loading={loadingMore}
                    disabled={loading}
                  >
                    {loadingMore ? 'Loading...' : `Load More (${total - callLogs.length} left)`}
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Empty */}
          {!loading && callLogs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
              <h4>No Call Logs</h4>
              <p>{searchText ? 'No results found' : 'No calls available'}</p>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default DashboardCallLogsDrawer;